// src/components/schedule/EventDetailEnrichment.tsx
//
// C2 — the canonical Event-detail enrichment surface (used by the unified
// Schedule detail). Progressive disclosure of PRODUCT-LEVEL facts:
//   • Zoom join/host + sync/provider state (incl. externally-deleted/error);
//   • INTENDED attendees (RSVP, EventAttendee) — kept visually DISTINCT from
//   • provider-OBSERVED attendance (EventParticipant/EventAttendanceObservation),
//     which is remote/provider evidence, never implied to be physical/in-person;
//   • recording/transcript availability (EventArtifact METADATA only).
//
// It shows no provider ids/keys, no media, no transcript text, no privileged
// URLs. Each section renders only when it has something to say.

import type { Event } from '../../types/api';
import { useGetEventAttendanceQuery, useGetEventArtifactsQuery } from '../../api/eventsApi';

const SYNC_LABEL: Record<string, { label: string; cls: string }> = {
  SYNCED: { label: 'Zoom connected', cls: 'bg-green-100 text-green-800' },
  PENDING: { label: 'Zoom syncing…', cls: 'bg-yellow-100 text-yellow-800' },
  ERROR: { label: 'Zoom sync error', cls: 'bg-red-100 text-red-800' },
  UNLINKED: { label: 'Zoom meeting removed', cls: 'bg-gray-100 text-gray-700' },
  DISABLED: { label: 'Zoom off', cls: 'bg-gray-100 text-gray-500' },
};

export default function EventDetailEnrichment({ event }: { event: Event }) {
  const zoomRelevant = event.zoomSyncEnabled || !!event.zoomJoinUrl || event.zoomSyncStatus === 'ERROR';
  const isVirtualOrHybrid = event.category === 'virtual' || event.category === 'hybrid';

  const { data: attendance } = useGetEventAttendanceQuery(event.id, { skip: !event.id });
  const { data: artifacts } = useGetEventArtifactsQuery(event.id, { skip: !event.id });

  const invited = event.attendees ?? [];
  const attendeeName = (a: (typeof invited)[number]) =>
    a.contact?.fullName || a.user?.fullName || a.externalEmail || 'Guest';

  return (
    <div className="space-y-4 min-w-0">
      {/* ── Zoom join / host / provider state ── */}
      {zoomRelevant && (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">Video meeting</h4>
            {event.zoomSyncStatus && SYNC_LABEL[event.zoomSyncStatus] && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${SYNC_LABEL[event.zoomSyncStatus].cls}`}>
                {SYNC_LABEL[event.zoomSyncStatus].label}
              </span>
            )}
          </div>
          {event.zoomJoinUrl ? (
            <a
              href={event.zoomJoinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors break-all"
            >
              Join Zoom meeting
            </a>
          ) : event.zoomSyncStatus === 'ERROR' ? (
            <p className="mt-1 text-xs text-red-700">The Zoom meeting could not be created. Try re-saving the event.</p>
          ) : event.zoomSyncStatus === 'UNLINKED' ? (
            <p className="mt-1 text-xs text-gray-600">This event’s Zoom meeting is no longer available in Zoom.</p>
          ) : event.zoomSyncStatus === 'PENDING' ? (
            <p className="mt-1 text-xs text-gray-500">The join link will appear once Zoom finishes creating the meeting.</p>
          ) : null}
        </div>
      )}

      {/* ── Invited (RSVP / intended) — distinct from observed ── */}
      {invited.length > 0 && (
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Invited · {invited.length}
          </h4>
          <ul className="space-y-1">
            {invited.slice(0, 12).map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 text-sm min-w-0">
                <span className="truncate text-gray-800">{attendeeName(a)}</span>
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                  {a.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Observed attendance (provider/remote) — never conflated with RSVP or physical ── */}
      {attendance && attendance.summary.observationCount > 0 && (
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Observed attendance
          </h4>
          <p className="text-sm text-gray-800">
            {attendance.summary.observationCount} observed
            {attendance.summary.resolvedCount > 0 && ` · ${attendance.summary.resolvedCount} matched`}
            {attendance.summary.unresolvedCount > 0 && ` · ${attendance.summary.unresolvedCount} unmatched`}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {event.category === 'hybrid'
              ? 'Remote (Zoom-observed) — does not include in-person attendance.'
              : 'From the video provider — separate from invited RSVPs.'}
          </p>
        </div>
      )}

      {/* ── Recording / transcript availability (metadata only) ── */}
      {isVirtualOrHybrid && artifacts && artifacts.summary.count > 0 && (
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">After the meeting</h4>
          <div className="flex flex-wrap gap-2">
            {artifacts.summary.hasRecording && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">Recording available</span>
            )}
            {artifacts.summary.hasTranscript && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">Transcript available</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
